"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BearWorkspaceCatalogFactory = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var sdk_backend_spi_1 = require("@gooddata/sdk-backend-spi");
var partition_1 = tslib_1.__importDefault(require("lodash/partition"));
var uniq_1 = tslib_1.__importDefault(require("lodash/uniq"));
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var keyBy_1 = tslib_1.__importDefault(require("lodash/keyBy"));
var api_model_bear_1 = require("@gooddata/api-model-bear");
var CatalogConverter_1 = require("../../../convertors/fromBackend/CatalogConverter");
var catalog_1 = require("./catalog");
var api_1 = require("../../../utils/api");
var errorHandling_1 = require("../../../utils/errorHandling");
var bearCatalogItemToCatalogItem = function (displayForms, attributes) {
    return function (item) {
        if (api_model_bear_1.GdcCatalog.isCatalogAttribute(item)) {
            return CatalogConverter_1.convertAttribute(item, displayForms, attributes);
        }
        else if (api_model_bear_1.GdcCatalog.isCatalogMetric(item)) {
            return CatalogConverter_1.convertMeasure(item);
        }
        return CatalogConverter_1.convertFact(item);
    };
};
var createLookups = function (displayFormsAndAttributes) {
    var _a = partition_1.default(displayFormsAndAttributes, api_model_bear_1.GdcMetadata.isWrappedAttribute), attributes = _a[0], displayForms = _a[1];
    var unwrappedDisplayForms = displayForms.map(function (df) { return df.attributeDisplayForm; });
    var attributeByUri = keyBy_1.default(attributes, function (item) { return item.attribute.meta.uri; });
    var attributeById = keyBy_1.default(attributes, function (item) { return item.attribute.meta.identifier; });
    var displayFormByUri = keyBy_1.default(unwrappedDisplayForms, function (item) { return item.meta.uri; });
    var displayFormById = keyBy_1.default(unwrappedDisplayForms, function (item) { return item.meta.identifier; });
    var attributeByDisplayFormUri = Object.keys(displayFormByUri).reduce(function (acc, displayFormUri) {
        var _a;
        var displayForm = displayFormByUri[displayFormUri];
        var attributeUri = displayForm.content.formOf;
        var attribute = attributeByUri[attributeUri];
        return tslib_1.__assign(tslib_1.__assign({}, acc), (_a = {}, _a[displayFormUri] = attribute, _a));
    }, {});
    return {
        attributeById: attributeById,
        attributeByDisplayFormUri: attributeByDisplayFormUri,
        displayFormById: displayFormById,
        displayFormByUri: displayFormByUri,
    };
};
var getProductionFlag = function (_a) {
    var production = _a.production, dataset = _a.dataset;
    // if production is undefined, leave it as is - it has meaning
    if (production === undefined) {
        return production;
    }
    // if a dataset is specified, production must be false
    var sanitizedProduction = !dataset && production;
    return sanitizedProduction ? 1 : 0;
};
var groupableCatalogItemTypes = ["attribute", "measure", "fact"];
var isGroupableCatalogItemType = function (type) { return groupableCatalogItemTypes.includes(type); };
var BearWorkspaceCatalogFactory = /** @class */ (function () {
    function BearWorkspaceCatalogFactory(authCall, workspace, options) {
        var _this = this;
        if (options === void 0) { options = {
            types: ["attribute", "measure", "fact", "dateDataset"],
            excludeTags: [],
            includeTags: [],
        }; }
        this.authCall = authCall;
        this.workspace = workspace;
        this.options = options;
        this.tagsAndDatasetIdsPromise = null;
        this.loadAllCatalogItemsAndMappings = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var _a, bearCatalogItems, bearUnlistedMetrics, bearDateDatasets, bearDisplayFormsAndAttributes, _b, attributeByDisplayFormUri, displayFormById, displayFormByUri, attributeById, catalogItems, catalogWithUnlisted, dateDatasets, allCatalogItems, measureById, factById, dateAttributeById;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, Promise.all([
                            this.loadBearCatalogItems(),
                            this.loadBearUnlistedMetrics(),
                            this.loadDateDatasets(),
                        ])];
                    case 1:
                        _a = _c.sent(), bearCatalogItems = _a[0], bearUnlistedMetrics = _a[1], bearDateDatasets = _a[2];
                        return [4 /*yield*/, this.loadBearDisplayFormsAndAttributes(bearCatalogItems, flatMap_1.default(bearDateDatasets, function (dd) { var _a; return (_a = dd.availableDateAttributes) !== null && _a !== void 0 ? _a : []; }))];
                    case 2:
                        bearDisplayFormsAndAttributes = _c.sent();
                        _b = createLookups(bearDisplayFormsAndAttributes), attributeByDisplayFormUri = _b.attributeByDisplayFormUri, displayFormById = _b.displayFormById, displayFormByUri = _b.displayFormByUri, attributeById = _b.attributeById;
                        catalogItems = bearCatalogItems.map(bearCatalogItemToCatalogItem(displayFormByUri, attributeById));
                        catalogWithUnlisted = this.withUpdatedUnlistedFlag(catalogItems, bearUnlistedMetrics);
                        dateDatasets = bearDateDatasets.map(function (dd) { return CatalogConverter_1.convertDateDataset(dd, attributeById); });
                        allCatalogItems = catalogWithUnlisted.concat(dateDatasets);
                        measureById = keyBy_1.default(catalogWithUnlisted.filter(sdk_backend_spi_1.isCatalogMeasure).map(function (el) { return el.measure; }), function (el) { return el.id; });
                        factById = keyBy_1.default(catalogItems.filter(sdk_backend_spi_1.isCatalogFact).map(function (el) { return el.fact; }), function (el) { return el.id; });
                        dateAttributeById = keyBy_1.default(flatMap_1.default(dateDatasets, function (dd) { return dd.dateAttributes; }), function (attr) { return attr.attribute.id; });
                        return [2 /*return*/, {
                                allCatalogItems: allCatalogItems,
                                mappings: {
                                    attributeById: attributeById,
                                    attributeByDisplayFormUri: attributeByDisplayFormUri,
                                    displayFormById: displayFormById,
                                    measureById: measureById,
                                    factById: factById,
                                    dateAttributeById: dateAttributeById,
                                },
                            }];
                }
            });
        }); };
        this.loadDateDatasets = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var _a, types, production, includeDateGranularities, includeDateDatasets, _b, includeTagsIds, excludeTagsIds, dataSetId, shouldReturnAllDateDataSets, result;
            var _this = this;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = this.options, types = _a.types, production = _a.production, includeDateGranularities = _a.includeDateGranularities;
                        includeDateDatasets = types.includes("dateDataset");
                        if (!includeDateDatasets) {
                            return [2 /*return*/, []];
                        }
                        return [4 /*yield*/, this.getTagsAndDatasetIds()];
                    case 1:
                        _b = _c.sent(), includeTagsIds = _b.includeTagsIds, excludeTagsIds = _b.excludeTagsIds, dataSetId = _b.dataSetId;
                        shouldReturnAllDateDataSets = !production && !dataSetId;
                        return [4 /*yield*/, this.authCall(function (sdk) {
                                return sdk.catalogue.loadDateDataSets(_this.workspace, {
                                    returnAllDateDataSets: shouldReturnAllDateDataSets,
                                    dataSetIdentifier: dataSetId,
                                    excludeObjectsWithTags: excludeTagsIds.length ? excludeTagsIds : undefined,
                                    includeObjectsWithTags: includeTagsIds.length ? includeTagsIds : undefined,
                                    includeDateGranularities: includeDateGranularities,
                                });
                            })];
                    case 2:
                        result = _c.sent();
                        return [2 /*return*/, result.dateDataSets];
                }
            });
        }); };
        this.loadBearCatalogItems = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var _a, types, dataset, compatibleBearItemTypes, _b, includeTagsIds, excludeTagsIds, dataSetId, bearItemTypes;
            var _this = this;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _a = this.options, types = _a.types, dataset = _a.dataset;
                        compatibleBearItemTypes = types.filter(CatalogConverter_1.isCompatibleCatalogItemType);
                        if (compatibleBearItemTypes.length === 0) {
                            return [2 /*return*/, []];
                        }
                        return [4 /*yield*/, this.getTagsAndDatasetIds()];
                    case 1:
                        _b = _c.sent(), includeTagsIds = _b.includeTagsIds, excludeTagsIds = _b.excludeTagsIds, dataSetId = _b.dataSetId;
                        bearItemTypes = compatibleBearItemTypes.map(CatalogConverter_1.convertItemType);
                        return [2 /*return*/, this.authCall(function (sdk) {
                                return sdk.catalogue.loadAllItems(_this.workspace, {
                                    types: bearItemTypes,
                                    includeWithTags: includeTagsIds.length ? includeTagsIds : undefined,
                                    excludeWithTags: excludeTagsIds.length ? excludeTagsIds : undefined,
                                    production: getProductionFlag(_this.options),
                                    csvDataSets: dataset ? [dataSetId] : [],
                                });
                            })];
                }
            });
        }); };
        /**
         * Loads unlisted metrics using /query resource. This is a shortcoming of the current
         * catalog items implementation. The unlisted items do not come back in /items resource - however,
         * they are needed to augment existing insights with metadata measures that MAY be unlisted.
         *
         * Previously, AD used to ignore catalog items when augmenting insights with bucket item metadata - instead
         * it was fetching the items as it found them. Now we try to position catalog as a single source of truth
         * and use just the catalog items for everything.
         */
        this.loadBearUnlistedMetrics = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var types, compatibleBearItemTypes, queryOptions;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                types = this.options.types;
                compatibleBearItemTypes = types.filter(function (item) { return item === "measure"; });
                if (compatibleBearItemTypes.length === 0) {
                    return [2 /*return*/, []];
                }
                queryOptions = {
                    category: "metric",
                    limit: 50,
                };
                return [2 /*return*/, this.authCall(function (sdk) {
                        return sdk.md
                            .getObjectsByQuery(_this.workspace, queryOptions)
                            .then(function (metrics) {
                            return metrics.filter(function (metric) { return metric.metric.meta.unlisted; }).map(CatalogConverter_1.convertMetric);
                        })
                            .catch(function (err) {
                            if (errorHandling_1.isApiResponseError(err) && err.response.status === 404) {
                                /*
                                 * Mock-server (mock-js) for GD platform does not support the md query resource.
                                 * Instead of enhancing the mock-js, code here opts to fallback to empty list
                                 * in case the query resource does not exist.
                                 */
                                return [];
                            }
                            throw err;
                        });
                    })];
            });
        }); };
        this.loadBearDisplayFormsAndAttributes = function (bearCatalogItems, dateDatasetAttributes) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var types, shouldLoadAttributes, bearCatalogAttributes, attributeUris, displayFormUris, dateAttributeUris;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                types = this.options.types;
                shouldLoadAttributes = types.some(function (type) { return type === "attribute" || type === "dateDataset"; });
                if (!shouldLoadAttributes) {
                    return [2 /*return*/, []];
                }
                bearCatalogAttributes = bearCatalogItems.filter(api_model_bear_1.GdcCatalog.isCatalogAttribute);
                attributeUris = bearCatalogAttributes.map(function (attr) { return attr.links.self; });
                displayFormUris = flatMap_1.default(bearCatalogAttributes, function (attr) {
                    var _a;
                    var geoPins = (_a = attr.links.geoPinDisplayForms) !== null && _a !== void 0 ? _a : [];
                    return tslib_1.__spreadArrays([attr.links.defaultDisplayForm], geoPins);
                });
                dateAttributeUris = dateDatasetAttributes.map(function (attr) { return attr.attributeMeta.uri; });
                return [2 /*return*/, this.authCall(function (sdk) {
                        return sdk.md.getObjects(_this.workspace, uniq_1.default(tslib_1.__spreadArrays(attributeUris, displayFormUris, dateAttributeUris)));
                    })];
            });
        }); };
        this.loadCatalogGroups = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var types, shouldLoadGroups, dataset, _a, includeTagsIds, excludeTagsIds, dataSetId, bearCatalogGroups;
            var _this = this;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        types = this.options.types;
                        shouldLoadGroups = types.some(isGroupableCatalogItemType);
                        if (!shouldLoadGroups) {
                            return [2 /*return*/, []];
                        }
                        dataset = this.options.dataset;
                        return [4 /*yield*/, this.getTagsAndDatasetIds()];
                    case 1:
                        _a = _b.sent(), includeTagsIds = _a.includeTagsIds, excludeTagsIds = _a.excludeTagsIds, dataSetId = _a.dataSetId;
                        return [4 /*yield*/, this.authCall(function (sdk) {
                                return sdk.catalogue.loadGroups(_this.workspace, {
                                    includeWithTags: includeTagsIds.length ? includeTagsIds : undefined,
                                    excludeWithTags: excludeTagsIds.length ? excludeTagsIds : undefined,
                                    production: getProductionFlag(_this.options),
                                    csvDataSets: dataset ? [dataSetId] : [],
                                });
                            })];
                    case 2:
                        bearCatalogGroups = _b.sent();
                        return [2 /*return*/, bearCatalogGroups.map(CatalogConverter_1.convertGroup)];
                }
            });
        }); };
        this.getTagsAndDatasetIds = function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            var _a, dataset, includeTags, excludeTags;
            return tslib_1.__generator(this, function (_b) {
                if (!this.tagsAndDatasetIdsPromise) {
                    _a = this.options, dataset = _a.dataset, includeTags = _a.includeTags, excludeTags = _a.excludeTags;
                    this.tagsAndDatasetIdsPromise = Promise.all([
                        api_1.objRefsToIdentifiers(includeTags, this.authCall),
                        api_1.objRefsToIdentifiers(excludeTags, this.authCall),
                        dataset ? api_1.objRefToIdentifier(dataset, this.authCall) : Promise.resolve(""),
                    ]).then(function (_a) {
                        var includeTagsIds = _a[0], excludeTagsIds = _a[1], dataSetId = _a[2];
                        return ({
                            dataSetId: dataSetId,
                            excludeTagsIds: excludeTagsIds,
                            includeTagsIds: includeTagsIds,
                        });
                    });
                }
                return [2 /*return*/, this.tagsAndDatasetIdsPromise];
            });
        }); };
        this.withUpdatedUnlistedFlag = function (catalogItems, bearUnlistedMetrics) {
            var catalogWithUnlisted = tslib_1.__spreadArrays(catalogItems);
            bearUnlistedMetrics.forEach(function (metric) {
                var existingMeasure = catalogItems.find(function (item) { return sdk_backend_spi_1.isCatalogMeasure(item) && item.measure.uri === metric.measure.uri; });
                if (existingMeasure) {
                    existingMeasure.measure.unlisted = true;
                }
            });
            return catalogWithUnlisted;
        };
    }
    BearWorkspaceCatalogFactory.prototype.withOptions = function (options) {
        var newOptions = tslib_1.__assign(tslib_1.__assign({}, this.options), options);
        return new BearWorkspaceCatalogFactory(this.authCall, this.workspace, newOptions);
    };
    BearWorkspaceCatalogFactory.prototype.forDataset = function (dataset) {
        return this.withOptions({
            dataset: dataset,
        });
    };
    BearWorkspaceCatalogFactory.prototype.forTypes = function (types) {
        return this.withOptions({
            types: types,
        });
    };
    BearWorkspaceCatalogFactory.prototype.includeTags = function (tags) {
        return this.withOptions({
            includeTags: tags,
        });
    };
    BearWorkspaceCatalogFactory.prototype.excludeTags = function (tags) {
        return this.withOptions({
            excludeTags: tags,
        });
    };
    BearWorkspaceCatalogFactory.prototype.load = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, _b, allCatalogItems, mappings, catalogGroups;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, Promise.all([
                            this.loadAllCatalogItemsAndMappings(),
                            this.loadCatalogGroups(),
                        ])];
                    case 1:
                        _a = _c.sent(), _b = _a[0], allCatalogItems = _b.allCatalogItems, mappings = _b.mappings, catalogGroups = _a[1];
                        return [2 /*return*/, new catalog_1.BearWorkspaceCatalog(this.authCall, this.workspace, catalogGroups, allCatalogItems, this.options, mappings)];
                }
            });
        });
    };
    return BearWorkspaceCatalogFactory;
}());
exports.BearWorkspaceCatalogFactory = BearWorkspaceCatalogFactory;
//# sourceMappingURL=factory.js.map